/** Unified document and audit trail types */

import { displayIdentity } from "@/lib/uuid";

export type DocumentCategory =
  | "Quote"
  | "Contract"
  | "Change Order"
  | "Disposition"
  | "QA Photo"
  | "Issue Photo"
  | "NTBO"
  | "Other";

export type DocumentStatus = "Draft" | "Sent" | "Viewed" | "Signed" | "Archived";

export interface UnifiedDocument {
  id: string;
  category: DocumentCategory;
  title: string;
  status: DocumentStatus;
  /** Reference IDs */
  jobId?: string;
  quoteId?: string;
  customerName: string;
  /** Version tracking */
  version: number;
  previousVersionId?: string;
  /** Metadata */
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  /** Optional content reference (data URL for signatures, etc.) */
  contentRef?: string;
  notes?: string;
  /**
   * Storage reference for uploaded files — format: "bucket::path/to/file"
   * Set for migration contracts and any attachment-backed documents.
   * DocumentCenter uses this to generate a signed URL for viewing/downloading the actual file.
   */
  storageRef?: string;
}

export type AuditActionType =
  | "quote_created"
  | "quote_updated"
  | "quote_presented"
  | "quote_won"
  | "quote_lost"
  | "contract_generated"
  | "contract_signed"
  | "change_order_created"
  | "change_order_signed"
  | "pricing_adjusted"
  | "credit_issued"
  | "credit_updated"
  | "job_created"
  | "job_stage_changed"
  | "job_completed"
  | "qa_submitted"
  | "issue_created"
  | "issue_resolved"
  | "material_ordered"
  | "dispatch_assigned"
  | "document_sent";

export interface AuditEntry {
  id: string;
  timestamp: string;
  user: string;
  actionType: AuditActionType;
  description: string;
  /** Related record references */
  jobId?: string;
  quoteId?: string;
  contractId?: string;
  changeOrderId?: string;
  creditId?: string;
  issueId?: string;
  /** Before/after for change tracking */
  previousValue?: string;
  newValue?: string;
}

/** Build unified documents from existing job data */
export function buildJobDocuments(
  job: {
    id: string;
    jobNumber?: string;
    customerName: string;
    contractDocument?: { id: string; status: string; createdAt: string; homeownerSignedAt?: string };
    changeOrders?: { id: string; orderNumber: number; status: string; createdAt: string; createdBy: string; signedAt?: string; summary: string }[];
    qaSubmitted: boolean;
    photos: string[];
    convertedFromQuoteId?: string;
  },
  quotes: { id: string; displayReference?: string; quoteNumber?: string; status: string; createdAt: string; createdBy: string; updatedAt: string; customerName: string }[],
  issues: { id: string; jobId: string; photo: string; dateCreated: string; source: string }[],
  attachments?: { id: string; fileName: string; createdAt: string; createdBy: string; attachmentType: string }[],
): UnifiedDocument[] {
  const docs: UnifiedDocument[] = [];

  // Quote document
  const relatedQuote = quotes.find(q => q.id === job.convertedFromQuoteId);
  if (relatedQuote) {
    const qStatus: DocumentStatus =
      (relatedQuote.status === "Won" || relatedQuote.status === "Converted") ? "Signed" :
      relatedQuote.status === "Presented" ? "Sent" :
      relatedQuote.status === "Draft" ? "Draft" : "Viewed";
    docs.push({
      id: `doc-quote-${relatedQuote.id}`,
      category: "Quote",
      title: `Quote ${relatedQuote.displayReference || relatedQuote.quoteNumber || relatedQuote.id}`,
      status: qStatus,
      jobId: job.id,
      quoteId: relatedQuote.id,
      customerName: job.customerName,
      version: 1,
      createdAt: relatedQuote.createdAt,
      createdBy: relatedQuote.createdBy,
      updatedAt: relatedQuote.updatedAt,
      contentRef: `/opportunities/${relatedQuote.id}`,
    });
  }

  // Contract document
  if (job.contractDocument) {
    const cStatus: DocumentStatus =
      job.contractDocument.status === "Signed" ? "Signed" :
      job.contractDocument.status === "Pending" ? "Sent" : "Draft";
    const contractDoc = job.contractDocument as typeof job.contractDocument & { filePath?: string; fileName?: string };
    docs.push({
      id: `doc-contract-${job.contractDocument.id}`,
      category: "Contract",
      title: contractDoc.fileName
        ? `Contract — ${contractDoc.fileName}`
        : `Contract — ${job.jobNumber || job.id}`,
      status: cStatus,
      jobId: job.id,
      quoteId: relatedQuote?.id,
      customerName: job.customerName,
      version: 1,
      createdAt: job.contractDocument.createdAt,
      createdBy: "System",
      updatedAt: job.contractDocument.homeownerSignedAt || job.contractDocument.createdAt,
      contentRef: relatedQuote ? `/opportunities/${relatedQuote.id}` : `/jobs/${job.id}?tab=financials`,
      storageRef: contractDoc.filePath ?? undefined,
    });
  }

  // Change orders
  if (job.changeOrders) {
    job.changeOrders.forEach(co => {
      const coStatus: DocumentStatus =
        co.status === "Signed" ? "Signed" :
        co.status === "Voided" ? "Archived" :
        co.status === "Pending Review" ? "Sent" : "Draft";
      docs.push({
        id: `doc-co-${co.id}`,
        category: "Change Order",
        title: `Change Order CO-${co.orderNumber}`,
        status: coStatus,
        jobId: job.id,
        customerName: job.customerName,
        version: 1,
        createdAt: co.createdAt,
        createdBy: co.createdBy,
        updatedAt: co.signedAt || co.createdAt,
        notes: co.summary,
      });
    });
  }

  // QA photos
  if (job.qaSubmitted && job.photos.length > 0) {
    docs.push({
      id: `doc-qa-${job.id}`,
      category: "QA Photo",
      title: `QA Photos (${job.photos.length})`,
      status: "Archived",
      jobId: job.id,
      customerName: job.customerName,
      version: 1,
      createdAt: new Date().toISOString(),
      createdBy: "Field Crew",
      updatedAt: new Date().toISOString(),
    });
  }

  // Issue photos
  const jobIssuesWithPhotos = issues.filter(i => i.jobId === job.id && i.photo);
  if (jobIssuesWithPhotos.length > 0) {
    docs.push({
      id: `doc-issue-photos-${job.id}`,
      category: "Issue Photo",
      title: `Issue Documentation (${jobIssuesWithPhotos.length})`,
      status: "Archived",
      jobId: job.id,
      customerName: job.customerName,
      version: 1,
      createdAt: jobIssuesWithPhotos[0].dateCreated,
      createdBy: jobIssuesWithPhotos[0].source,
      updatedAt: jobIssuesWithPhotos[jobIssuesWithPhotos.length - 1].dateCreated,
    });
  }

  // NTBO / permit attachments
  if (attachments) {
    const permitAtts = attachments.filter(a => a.attachmentType === "permit");
    if (permitAtts.length > 0) {
      docs.push({
        id: `doc-ntbo-${job.id}`,
        category: "NTBO",
        title: `NTBO Documents (${permitAtts.length})`,
        status: "Archived",
        jobId: job.id,
        customerName: job.customerName,
        version: 1,
        createdAt: permitAtts[0].createdAt,
        createdBy: permitAtts[0].createdBy,
        updatedAt: permitAtts[permitAtts.length - 1].createdAt,
      });
    }
  }

  return docs.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Build audit trail from existing job data */
export function buildJobAuditTrail(
  job: {
    id: string;
    activityHistory: { id: string; date: string; action: string; user: string; noteType?: string }[];
    contractDocument?: { id: string; createdAt: string; homeownerSignedAt?: string; status: string };
    changeOrders?: { id: string; orderNumber: number; createdAt: string; createdBy: string; signedAt?: string; changeAmount: number; revisedContractValue: number }[];
    convertedFromQuoteId?: string;
    qaSubmitted: boolean;
  },
  quotes: { id: string; displayReference: string; createdAt: string; createdBy: string; status: string; presentedAt?: string; wonAt?: string; lostAt?: string; lostReason?: string }[],
  issues: { id: string; jobId: string; dateCreated: string; owner: string; issueType: string; status: string; dateResolved?: string }[],
  credits: { id: string; jobId: string; dateCreated: string; createdBy: string; creditAmount: number; creditType: string; status: string }[],
): AuditEntry[] {
  const entries: AuditEntry[] = [];
  const mapActivityToActionType = (actionText: string): AuditActionType => {
    const action = actionText.toLowerCase();
    if (action.includes("qa")) return "qa_submitted";
    if (action.includes("material") || action.includes("order")) return "material_ordered";
    if (action.includes("dispatch") || action.includes("assign")) return "dispatch_assigned";
    if (action.includes("document") || action.includes("sent")) return "document_sent";
    if (action.includes("complete") || action.includes("closed")) return "job_completed";
    if (action.includes("created") || action.includes("sale closed")) return "job_created";
    return "job_stage_changed";
  };

   // Quote events
   const relatedQuote = quotes.find(q => q.id === job.convertedFromQuoteId);
   if (relatedQuote) {
     const quoteLabel = displayIdentity(relatedQuote.displayReference, relatedQuote.quoteNumber ?? "Quote");
     entries.push({
       id: `audit-q-created-${relatedQuote.id}`,
       timestamp: relatedQuote.createdAt,
       user: relatedQuote.createdBy,
       actionType: "quote_created",
       description: `Quote ${quoteLabel} created`,
       jobId: job.id,
       quoteId: relatedQuote.id,
     });
     if (relatedQuote.presentedAt) {
       entries.push({
         id: `audit-q-presented-${relatedQuote.id}`,
         timestamp: relatedQuote.presentedAt,
         user: relatedQuote.createdBy,
         actionType: "quote_presented",
         description: `Quote ${quoteLabel} presented to customer`,
         quoteId: relatedQuote.id,
         jobId: job.id,
       });
     }
     if (relatedQuote.wonAt) {
       entries.push({
         id: `audit-q-won-${relatedQuote.id}`,
         timestamp: relatedQuote.wonAt,
         user: relatedQuote.createdBy,
         actionType: "quote_won",
         description: `Quote ${quoteLabel} won — job created`,
         quoteId: relatedQuote.id,
         jobId: job.id,
       });
     }
     if (relatedQuote.lostAt) {
       entries.push({
         id: `audit-q-lost-${relatedQuote.id}`,
         timestamp: relatedQuote.lostAt,
         user: relatedQuote.createdBy,
         actionType: "quote_lost",
         description: `Quote ${quoteLabel} lost — ${relatedQuote.lostReason || "no reason"}`,
         quoteId: relatedQuote.id,
       });
     }
   }

  // Contract events
  if (job.contractDocument) {
    entries.push({
      id: `audit-contract-gen-${job.contractDocument.id}`,
      timestamp: job.contractDocument.createdAt,
      user: "System",
      actionType: "contract_generated",
      description: "Contract generated from quote",
      jobId: job.id,
      contractId: job.contractDocument.id,
    });
    if (job.contractDocument.homeownerSignedAt) {
      entries.push({
        id: `audit-contract-signed-${job.contractDocument.id}`,
        timestamp: job.contractDocument.homeownerSignedAt,
        user: "Homeowner",
        actionType: "contract_signed",
        description: "Contract signed by homeowner",
        jobId: job.id,
        contractId: job.contractDocument.id,
      });
    }
  }

  // Change orders
  if (job.changeOrders) {
    job.changeOrders.forEach(co => {
      entries.push({
        id: `audit-co-created-${co.id}`,
        timestamp: co.createdAt,
        user: co.createdBy,
        actionType: "change_order_created",
        description: `Change Order CO-${co.orderNumber} created — ${co.changeAmount >= 0 ? "+" : ""}$${co.changeAmount.toLocaleString()}`,
        jobId: job.id,
        changeOrderId: co.id,
      });
      if (co.signedAt) {
        entries.push({
          id: `audit-co-signed-${co.id}`,
          timestamp: co.signedAt,
          user: "Homeowner",
          actionType: "change_order_signed",
          description: `Change Order CO-${co.orderNumber} signed — revised total $${co.revisedContractValue.toLocaleString()}`,
          jobId: job.id,
          changeOrderId: co.id,
        });
      }
    });
  }

  // QA
  if (job.qaSubmitted) {
    entries.push({
      id: `audit-qa-${job.id}`,
      timestamp: job.activityHistory.find(a => a.action.toLowerCase().includes("qa"))?.date || new Date().toISOString(),
      user: "Field Crew",
      actionType: "qa_submitted",
      description: "Install QA checklist submitted",
      jobId: job.id,
    });
  }

  // Issues
  const jobIssues = issues.filter(i => i.jobId === job.id);
  jobIssues.forEach(issue => {
    entries.push({
      id: `audit-issue-created-${issue.id}`,
      timestamp: issue.dateCreated,
      user: issue.owner,
      actionType: "issue_created",
      description: `Issue created: ${issue.issueType}`,
      jobId: job.id,
      issueId: issue.id,
    });
    if (issue.dateResolved) {
      entries.push({
        id: `audit-issue-resolved-${issue.id}`,
        timestamp: issue.dateResolved,
        user: issue.owner,
        actionType: "issue_resolved",
        description: `Issue resolved: ${issue.issueType}`,
        jobId: job.id,
        issueId: issue.id,
      });
    }
  });

  // Credits
  const jobCredits = credits.filter(c => c.jobId === job.id);
  jobCredits.forEach(cr => {
    entries.push({
      id: `audit-credit-${cr.id}`,
      timestamp: cr.dateCreated,
      user: cr.createdBy,
      actionType: "credit_issued",
      description: `${cr.creditType} issued — $${cr.creditAmount.toLocaleString()}`,
      jobId: job.id,
      creditId: cr.id,
    });
  });

  // Job activity history (operational timeline source of truth)
  job.activityHistory.forEach((activity, index) => {
    const actionType = mapActivityToActionType(activity.action || "");
    const safeActivityId = String(activity.id || index);
    const safeTimestamp = activity.date || new Date().toISOString();
    entries.push({
      id: `audit-activity-${job.id}-${safeActivityId}-${safeTimestamp}`,
      timestamp: safeTimestamp,
      user: activity.user || "System",
      actionType,
      description: activity.action || "Job activity updated",
      jobId: job.id,
    });
  });

  return entries.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}
